/**
 * F45 (Wave 2D) — engineering-authored city intensity visualization.
 *
 * The plan (§11) calls for "MapView clustered intensity pins" on the geographic
 * panel, but @bravemove/ui-web `MapView` strictly requires lat/lng `VenueListItem[]`
 * + a Google Maps `apiKey`, and the frozen `AnalyticsCityRow` carries only
 * `{ cityCode, cityName, count }` (no coordinates — D15 aggregate-only). Rather
 * than fabricate coordinates that are not in the contract, this renders a
 * horizontal intensity-bar list (count → accent-bar width over the max). The
 * sortable city `Table` beside it is the authoritative data view.
 *
 * TODO(F45 design-polish): when a city→centroid map lands (F04 cities carry a
 * centroid), swap this for a real MapView choropleth (meta-plan 2E).
 */
import { useMemo } from 'react';
import type { AnalyticsCityRow } from '@bravemove/types';

export interface CityIntensityProps {
  readonly cities: readonly AnalyticsCityRow[];
  readonly ariaLabel?: string;
}

export function CityIntensity({ cities, ariaLabel }: CityIntensityProps) {
  const max = useMemo(() => cities.reduce((m, c) => Math.max(m, c.count), 0), [cities]);
  return (
    <div
      className="space-y-2"
      data-testid="analytics-city-intensity"
      role="img"
      aria-label={ariaLabel}
    >
      {cities.map((c) => {
        const pct = max === 0 ? 0 : Math.round((c.count / max) * 100);
        return (
          <div key={c.cityCode} className="flex items-center gap-3" data-city={c.cityCode}>
            <span className="w-24 shrink-0 truncate text-[12px] text-ink-2" title={c.cityName}>
              {c.cityName}
            </span>
            <div className="h-3 flex-1 overflow-hidden rounded-full bg-surface-2">
              <div
                className="h-full rounded-full bg-accent"
                style={{ width: `${Math.max(pct, c.count > 0 ? 4 : 0)}%` }}
              />
            </div>
            <span className="w-12 shrink-0 text-end font-mono text-[12px] tabular-nums text-ink">
              {c.count}
            </span>
          </div>
        );
      })}
    </div>
  );
}
